<template>
    <div class="follow-panel">
        <div class="introduction">

            <site-icon class="icon" name="users" scale="13"></site-icon>

            <div v-if="owner" class="title-panel">
                <h2 class="title">{{ title }}</h2>
                <p>{{ t[`${followType}_explanation`] }}</p>
            </div>
            <div v-if="!owner" class="title-panel">
                <h2 class="title">{{ title.replace('%count', numberOfFollowers) }}</h2>
                <p>{{ t[`${followType}_explanation`] }}</p>
            </div>

            <!-- Router & Filter Buttons -->
            <router-link v-if="followType === 'following'" tag="a" class="btn btn-primary" :to="{ name: 'followers' }">
                {{ t.view_followers }}
            </router-link>
            <router-link v-if="followType === 'followers'" tag="a" class="btn btn-primary" :to="{ name: 'following' }">
                {{ t.view_following }}
            </router-link>
            <button v-if="isMobile" class="btn btn-primary pull-right" v-collapse="'#control-panel'">{{ t.show_filters }}</button>

            <!-- Filters - Hidden via Collapse on Mobile Devices -->
            <div id="control-panel" :class="['control-panel', {'collapse': isMobile, 'pull-right': !isMobile}]"  >
                <div class="form-inline">
                    <label for="pagination-show">{{ t.show }}</label>
                    <select class="form-control" v-model.number="limit" @change="fetch()">
                        <option selected :value="14">14</option>
                        <option :value="28">28</option>
                        <option :value="56">56</option>
                        <option :value="112">112</option>
                    </select>
                    <template>
                        <div class="input-group">
                            <input type="text" v-model="filter" @keyup.enter="fetch('filter')" class="form-control" :placeholder="t.username">
                            <span class="input-group-btn">
                                <button class="btn btn-default btn-primary" @click="fetch('filter')">{{ t.go }}</button>
                            </span>
                        </div>
                    </template>
                    <select v-model="sort" @change="fetch('sort')" class="form-control">
                        <option value="identity">{{ t.username }}</option>
                        <option value="online">{{ t.online }}</option>
                    </select>
                </div>
            </div>
        </div>

        <loading v-if="loading"></loading>

        <!-- Users list -->
        <div v-if="following.length && !loading" class="users-list pull-left">
            <div class="users profile-list">
                <user v-for="follower in following" :user="follower" :key="follower.id"
                    :owner="owner" :show-location="false" :showRequestPhoto="false">
                    <user-menu slot="menu" :user="follower" :view-profile="true"></user-menu>
                </user>
            </div>
            <pagination v-if="following.length && following.length < this.numberOfFollowers" :page="page"
            :total="totalPages" :top="scrollTop" @page-changed="pageChange"></pagination>
        </div>

        <div v-if="!following.length && !loading" class="no-users">
            <site-icon class="icon" name="search" scale="3"></site-icon>
            <p>{{ t.empty_following }}</p>
        </div>

    </div>
</template>

<script>
import 'vue-awesome/icons/users';
import 'vue-awesome/icons/search';
import { mapState } from 'vuex';
import { getCookie } from 'Site/js/util/cookie';
import ajax from 'Site/js/util/ajax.fetch';
import collapseDirective from 'Site/directive/collapse';
import Loading from 'Site/component/Loading.vue';
import Pagination from 'Site/component/Pagination.vue';
import User from 'Profile/component/User.vue';
import UserMenu from 'Profile/component/UserMenu.vue';

export default {
    name: 'FollowPanel',
    components: {
        Loading,
        Pagination,
        User,
        UserMenu
    },
    computed: {
        ...mapState({
            owner: state => state.config.data.owner,
            routerConfig: state => state.config.data.router,
            t: state => state.config.translate.profile.follow,
            user: state => state.config.data.userId,
            username: state => state.config.data.userName,
            isMobile: state => state.isMobile,
        }),
        offset() {
            return (this.page * this.limit) - this.limit;
        },
        page() {
            return parseInt(this.$route.params.page, 10) || 1;
        },
        totalPages() {
            return Math.ceil(this.numberOfFollowers / this.limit);
        },
        title() {
            if (this.owner) {
                if (this.numberOfFollowers === 1) {
                    return this.t[`${this.followType}_title_singular`].replace('%count', this.numberOfFollowers);
                }
                return this.t[`${this.followType}_title_plural`].replace('%count', this.numberOfFollowers);
            }
            if (this.numberOfFollowers === 1) {
                return this.t[`${this.followType}_title_singular_other`].replace('%count', this.numberOfFollowers).replace('%user', this.username);
            }
            return this.t[`${this.followType}_title_plural_other`].replace('%count', this.numberOfFollowers).replace('%user', this.username);
        }
    },
    data() {
        return {
            filter: '',
            following: [],
            isFilterChange: false,
            limit: parseInt(getCookie('profile-num-results'), 10) || 14,
            loading: false,
            loggedOut: this.$store.state.config.loggedOut || false,
            numberOfFollowers: '...',
            scrollTop: document.getElementById('profile-vue').offsetHeight || 0,
            sort: 'identity',
        };
    },
    directives: {
        collapse: collapseDirective
    },
    methods: {
        fetch(trigger = null) {
            this.loading = true;
            if (trigger) {
                this.isFilterChange = true;
                this.pageChange(1);
            }
            ajax.get('following', {
                'max-results': this.limit,
                'start-index': this.offset,
                filter: this.filter,
                searching: this.followType,
                sort: this.sort,
                user: this.user
            }).then((response) => {
                this.following = response.items;
                this.loading = false;
                this.numberOfFollowers = response.totalItems;
                this.isFilterChange = false;
            });
        },
        pageChange(page) {
            this.$router.push({ path: `${this.routerConfig[`${this.followType}`]}/${this.routerConfig.parts.page}/${page}` });
        },
    },
    created() {
        const setData = (settings, json = false) => {
            if (json) {
                settings = JSON.parse(settings);
            }
            this.following = settings.data.following;
            this.loading = false;
            this.numberOfFollowers = settings.data.numberOfFollowing;
        };
        if (document.querySelector('#follow-json')) {
            const settings = document.querySelector('#follow-json').textContent;
            if (settings) {
                setData(settings, true);
            }
        } else {
            this.fetch();
        }
    },
    watch: {
        $route: function watchRoute(to, from) {
            if (from.name !== to.name) {
                this.numberOfFollowers = '...';
            }
            // only fetch on route change if not user filter event
            if (!this.isFilterChange) {
                this.fetch();
            }
            this.isFilterChange = false;
        }
    },
    props: {
        followType: String,
    }
};
</script>

<style lang="scss">
@import "~Site/scss/variables";

.follow-panel {
    .introduction {
        padding: 20px 5px;
        position: relative;
        width: 100%;
        .icon {
            opacity: 0.05;
            position: absolute;
            right: 0;
            top: 0;
            transform: rotate(-13deg);
            z-index: -1;
        }
        .title-panel {
            .title {
                font-weight: 400;
                font-size: 2.65em;
            }
            p {
                font-size: 1.2em;
                line-height: 1.88;
            }
        }
        .control-panel {
            label {
                margin: 10px 5px 0 5px;
            }
            .input-group,
            select  {
                margin-bottom: 10px;
            }
        }
    }
    .users-list,
    .no-users {
        min-height: 200px;
        width: 100%;
    }
    .users-list {
        .users {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-around;
        }
    }
    .no-users {
        text-align: center;
        padding-top: 40px;
        font-size: 1.2em;
        opacity: 0.25;
    }
}
</style>

